import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * Gestion du consentement cookies (nLPD / RGPD) couplée à
 * Google Consent Mode v2 et au dataLayer de Google Tag Manager.
 *
 * Catégories :
 *  - necessary : toujours actif (fonctionnement du site, panier, paiement).
 *  - analytics : mesure d'audience (Google Analytics 4, Microsoft Clarity).
 *  - marketing : publicité / remarketing.
 *
 * Le choix est mémorisé 12 mois dans un cookie `cbjm_consent`.
 */

export const CONSENT_COOKIE = 'cbjm_consent';
export const CONSENT_VERSION = 1;
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 12 mois en secondes

// ID du projet Microsoft Clarity (défini dans .env : GATSBY_CLARITY_ID=xxxxxxxxxx).
const CLARITY_ID = process.env.GATSBY_CLARITY_ID;

const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  marketing: false,
};

export const ConsentContext = createContext({
  consent: DEFAULT_CONSENT,
  decided: false,
  showBanner: false,
  acceptAll: () => {},
  rejectAll: () => {},
  savePreferences: () => {},
  openPreferences: () => {},
  closeBanner: () => {},
});

// --- Utilitaires cookie (guardés SSR) ---------------------------------------

function readConsentCookie() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${CONSENT_COOKIE}=`));
  if (!match) return null;
  try {
    const value = decodeURIComponent(match.split('=').slice(1).join('='));
    const parsed = JSON.parse(value);
    // On ignore un consentement d'une ancienne version (ex: nouvelles catégories).
    if (parsed.v !== CONSENT_VERSION) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function writeConsentCookie(consent) {
  if (typeof document === 'undefined') return;
  const payload = {
    v: CONSENT_VERSION,
    necessary: true,
    analytics: !!consent.analytics,
    marketing: !!consent.marketing,
    ts: Date.now(),
  };
  const secure = typeof window !== 'undefined' &&
    window.location &&
    window.location.protocol === 'https:'
    ? '; Secure'
    : '';
  document.cookie =
    `${CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(payload))}` +
    `; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
}

// --- Pont vers Google Consent Mode v2 / dataLayer ---------------------------

// Charge le tag Microsoft Clarity une seule fois, uniquement après consentement.
function loadClarity() {
  if (typeof window === 'undefined' || !CLARITY_ID) return;
  if (window.__cbjmClarityLoaded) return;
  window.__cbjmClarityLoaded = true;
  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = 'https://www.clarity.ms/tag/' + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, 'clarity', 'script', CLARITY_ID);
}

function pushConsentToGoogle(consent) {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }

  const analytics = consent.analytics ? 'granted' : 'denied';
  const marketing = consent.marketing ? 'granted' : 'denied';

  gtag('consent', 'update', {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  });

  // Évènement générique : sert de déclencheur aux balises dans GTM
  // (utile notamment pour Microsoft Clarity qui n'utilise pas Consent Mode).
  window.dataLayer.push({
    event: 'cookie_consent_update',
    consent_analytics: consent.analytics,
    consent_marketing: consent.marketing,
  });

  // Microsoft Clarity : chargé uniquement après consentement à la mesure d'audience.
  if (consent.analytics) {
    loadClarity();
  }
  // Signale le choix de consentement à Clarity s'il est déjà chargé.
  if (typeof window.clarity === 'function') {
    window.clarity('consent', !!consent.analytics);
  }
}

// --- Provider ----------------------------------------------------------------

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [decided, setDecided] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  // Au montage : on lit le choix mémorisé et on le rejoue vers Google.
  useEffect(() => {
    const stored = readConsentCookie();
    if (stored) {
      const restored = {
        necessary: true,
        analytics: !!stored.analytics,
        marketing: !!stored.marketing,
      };
      setConsent(restored);
      setDecided(true);
      setShowBanner(false);
      pushConsentToGoogle(restored);
    } else {
      setShowBanner(true);
    }
  }, []);

  const persist = useCallback((next) => {
    const full = { necessary: true, ...next };
    setConsent(full);
    setDecided(true);
    setShowBanner(false);
    writeConsentCookie(full);
    pushConsentToGoogle(full);
  }, []);

  const acceptAll = useCallback(
    () => persist({ analytics: true, marketing: true }),
    [persist]
  );

  const rejectAll = useCallback(
    () => persist({ analytics: false, marketing: false }),
    [persist]
  );

  const savePreferences = useCallback(
    (prefs) =>
      persist({
        analytics: !!prefs.analytics,
        marketing: !!prefs.marketing,
      }),
    [persist]
  );

  const openPreferences = useCallback(() => setShowBanner(true), []);
  const closeBanner = useCallback(() => setShowBanner(false), []);

  const value = useMemo(
    () => ({
      consent,
      decided,
      showBanner,
      acceptAll,
      rejectAll,
      savePreferences,
      openPreferences,
      closeBanner,
    }),
    [
      consent,
      decided,
      showBanner,
      acceptAll,
      rejectAll,
      savePreferences,
      openPreferences,
      closeBanner,
    ]
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
};

export const useConsent = () => useContext(ConsentContext);

export default ConsentProvider;
